/**
 * GCU (General Check-Up) domain constants — the 9-step flow that is the
 * backbone of the product (intake.md §D, decisions.md §Alur GCU & AI).
 *
 * These power CR2–CR6. They live in shared so the Admin "GCU Monitor"
 * (step N of 9) and the Owner stepper render from a single source of truth.
 */

/** The canonical 9 steps of a GCU run (1-indexed in the UI). */
export const GCU_STEPS = [
  'identity_pic', //          1. Owner identity + PIC per division
  'upload_documents', //      2. Upload initial data (finance/SOP/akad/...)
  'draft_pic_questionnaire', //3. AI draft per-PIC questionnaire (hybrid review)
  'send_pic_questionnaire', // 4. Send final questionnaire to each PIC (magic-link)
  'collect_pic_answers', //   5. PIC fills → received by Nine Co
  'draft_owner_questionnaire',//6. AI draft Owner verification questionnaire (hybrid)
  'collect_owner_answers', //  7. Owner fills & returns
  'generate_report', //       8. AI report → internal review → send
  'completed', //             9. Report received → process complete
] as const;
export type GcuStep = (typeof GCU_STEPS)[number];

/** 1-based step number used by the UI stepper / Admin monitor. */
export function gcuStepNumber(step: GcuStep): number {
  return GCU_STEPS.indexOf(step) + 1;
}
export const GCU_STEP_COUNT = GCU_STEPS.length;

export function isGcuStep(value: string): value is GcuStep {
  return (GCU_STEPS as readonly string[]).includes(value);
}

/** Document categories uploaded at intake (CR2). Drives doc_type scoping in RAG. */
export const DOC_TYPES = [
  'financial_report', // laporan keuangan 1 tahun
  'agreement', //        akad / perjanjian (internal & vendor)
  'sop', //              SOP (PDF)
  'marketing_program', //program pemasaran berjalan
  'org_structure', //    struktur organisasi
  'hr_data', //          jumlah karyawan & data SDM
  'other',
] as const;
export type DocType = (typeof DOC_TYPES)[number];

export function isDocType(value: string): value is DocType {
  return (DOC_TYPES as readonly string[]).includes(value);
}

/**
 * Document lifecycle (CR2 upload + parse slice + CR3 indexing):
 *   pending_upload → uploaded → queued → parsing → parsed → indexed(CR3) → failed
 *                                              ↘ needs_ocr (scanned PDF, manual)
 *
 * - pending_upload: row created, presigned PUT issued, object not yet confirmed in R2.
 * - uploaded:       HEAD-verified in R2 (exists + size matches). Eligible for analyze.
 * - queued:         a parse_document job has been enqueued (analyze).
 * - parsing:        the consumer is extracting text/pages.
 * - parsed:         text extracted + chunked; ready to embed (CR3).
 * - indexed:        chunks embedded into pgvector (CR3).
 * - needs_ocr:      a scanned/image-only PDF yielded no extractable text and could
 *                   NOT be OCR'd automatically (in-Worker PDF rasterization is not
 *                   feasible on workerd today — see pipeline/ocr.ts). Surfaced so the
 *                   Nine Co team supplies a text version / handles it manually.
 * - failed:         parse failed (unsupported/corrupt/error); see documents.parseError.
 */
export const DOC_STATUSES = [
  'pending_upload',
  'uploaded',
  'queued',
  'parsing',
  'parsed',
  'indexed',
  'needs_ocr',
  'failed',
] as const;
export type DocStatus = (typeof DOC_STATUSES)[number];

export function isDocStatus(value: string): value is DocStatus {
  return (DOC_STATUSES as readonly string[]).includes(value);
}

/** GCU process lifecycle (one per company). */
export const GCU_PROCESS_STATUSES = ['in_progress', 'completed', 'cancelled'] as const;
export type GcuProcessStatus = (typeof GCU_PROCESS_STATUSES)[number];

export function isGcuProcessStatus(value: string): value is GcuProcessStatus {
  return (GCU_PROCESS_STATUSES as readonly string[]).includes(value);
}

/** Questionnaire rounds (CR3). Round 1 = per-PIC, round 2 = Owner verification. */
export const QUESTIONNAIRE_ROUNDS = ['pic', 'owner_verification'] as const;
export type QuestionnaireRound = (typeof QUESTIONNAIRE_ROUNDS)[number];

export function isQuestionnaireRound(value: string): value is QuestionnaireRound {
  return (QUESTIONNAIRE_ROUNDS as readonly string[]).includes(value);
}

/**
 * Questionnaire lifecycle (CR3 hybrid AI → review → send → collect):
 *   draft → in_review → approved → sent → collecting → completed (or failed)
 * - draft      : enqueued for AI generation (or being team-edited before review).
 * - in_review  : AI draft produced; Nine Co team is editing/reviewing.
 * - approved   : team approved (transient; immediately → sent (pic) / collecting (owner)).
 * - sent       : PIC round — magic-links issued + notifications enqueued.
 * - collecting : owner round — owner can answer in-app (no link).
 * - completed  : all required answers submitted.
 * - failed     : AI generation failed (generationError set on the row).
 */
export const QUESTIONNAIRE_STATUSES = [
  'draft',
  'in_review',
  'approved',
  'sent',
  'collecting',
  'completed',
  'failed',
] as const;
export type QuestionnaireStatus = (typeof QUESTIONNAIRE_STATUSES)[number];

export function isQuestionnaireStatus(value: string): value is QuestionnaireStatus {
  return (QUESTIONNAIRE_STATUSES as readonly string[]).includes(value);
}

/** Question input types (CR3). scale = numeric slider, choice = options, open = free text. */
export const QUESTION_TYPES = ['scale', 'choice', 'open'] as const;
export type QuestionType = (typeof QUESTION_TYPES)[number];

export function isQuestionType(value: string): value is QuestionType {
  return (QUESTION_TYPES as readonly string[]).includes(value);
}

/** Provenance of a question (hybrid AI → team review). */
export const QUESTION_SOURCES = ['ai', 'team_edited', 'team_added'] as const;
export type QuestionSource = (typeof QUESTION_SOURCES)[number];

export function isQuestionSource(value: string): value is QuestionSource {
  return (QUESTION_SOURCES as readonly string[]).includes(value);
}

/** Who answered (PIC via magic-link, or the Owner in-app). */
export const ANSWER_RESPONDENT_TYPES = ['pic', 'owner'] as const;
export type AnswerRespondentType = (typeof ANSWER_RESPONDENT_TYPES)[number];

export function isAnswerRespondentType(value: string): value is AnswerRespondentType {
  return (ANSWER_RESPONDENT_TYPES as readonly string[]).includes(value);
}

/** Notification channels for questionnaire distribution (CR3). */
export const DELIVERY_CHANNELS = ['email', 'whatsapp'] as const;
export type DeliveryChannel = (typeof DELIVERY_CHANNELS)[number];

/** Per-channel delivery outcome. */
export const DELIVERY_STATUSES = ['queued', 'sent', 'failed'] as const;
export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number];

/** SWOT axes used by the GCU report (CR4). */
export const SWOT_AXES = ['strength', 'weakness', 'opportunity', 'threat'] as const;
export type SwotAxis = (typeof SWOT_AXES)[number];

export function isSwotAxis(value: string): value is SwotAxis {
  return (SWOT_AXES as readonly string[]).includes(value);
}

/** Business-health status semantics (design-spec.md §1 semantic status). */
export const HEALTH_STATUS = ['healthy', 'watch', 'critical'] as const;
export type HealthStatus = (typeof HEALTH_STATUS)[number];

export function isHealthStatus(value: string): value is HealthStatus {
  return (HEALTH_STATUS as readonly string[]).includes(value);
}

/**
 * GCU report lifecycle (CR4 hybrid AI → review → approve+finalize → send):
 *   draft → generating → in_review → approved → sent (or failed)
 * - draft      : report row created (get-or-create on generate); job enqueued.
 * - generating : the consumer holds the idempotency lock (only one worker runs).
 * - in_review  : AI draft produced; Nine Co team is editing the report.
 * - approved   : team approved+finalized; PDF compose enqueued/done.
 * - sent       : report delivered to the Owner (email + in-app); GCU complete.
 * - failed     : AI generation failed (generationError set on the row).
 */
export const REPORT_STATUSES = [
  'draft',
  'generating',
  'in_review',
  'approved',
  'sent',
  'failed',
] as const;
export type ReportStatus = (typeof REPORT_STATUSES)[number];

export function isReportStatus(value: string): value is ReportStatus {
  return (REPORT_STATUSES as readonly string[]).includes(value);
}

/**
 * The 4 business-health areas scored in the GCU report (intake.md / PoC cr4a).
 * Keys of `reports.areaScores` — each 0–100.
 */
export const REPORT_AREAS = ['strategis', 'operasional', 'keuangan', 'pemasaran'] as const;
export type ReportArea = (typeof REPORT_AREAS)[number];

export function isReportArea(value: string): value is ReportArea {
  return (REPORT_AREAS as readonly string[]).includes(value);
}

/** Provenance of a report finding (hybrid AI → team review). Mirrors QUESTION_SOURCES. */
export const FINDING_SOURCES = ['ai', 'team_edited', 'team_added'] as const;
export type FindingSource = (typeof FINDING_SOURCES)[number];

export function isFindingSource(value: string): value is FindingSource {
  return (FINDING_SOURCES as readonly string[]).includes(value);
}

/* ======================= CR5 — Rekomendasi & Coaching ======================= */

/**
 * Catalog program areas (CR5). The 4 GCU report areas + 'general' so a
 * cross-cutting program (e.g. leadership) can match any area. The join target
 * for `report.recommendations[].area`.
 */
export const PROGRAM_AREAS = [...REPORT_AREAS, 'general'] as const;
export type ProgramArea = (typeof PROGRAM_AREAS)[number];

export function isProgramArea(value: string): value is ProgramArea {
  return (PROGRAM_AREAS as readonly string[]).includes(value);
}

/**
 * Priority band of a program recommendation (CR5, poc cr5a "Prioritas
 * Tinggi/Sedang"). Derived deterministically from matchPercent + the weakest
 * area; coach-overridable.
 */
export const RECOMMENDATION_PRIORITIES = ['high', 'medium', 'low'] as const;
export type RecommendationPriority = (typeof RECOMMENDATION_PRIORITIES)[number];

export function isRecommendationPriority(value: string): value is RecommendationPriority {
  return (RECOMMENDATION_PRIORITIES as readonly string[]).includes(value);
}

/**
 * Lifecycle of a program recommendation (CR5):
 *   suggested → offered → selected (or dismissed)
 * - suggested : derived by generate (AI/deterministic); internal review pending.
 * - offered   : coach has surfaced it to the Owner.
 * - selected  : Owner picked it → an engagement was created (frozen rec).
 * - dismissed : coach dropped it (hidden from the Owner).
 */
export const RECOMMENDATION_STATUSES = ['suggested', 'offered', 'selected', 'dismissed'] as const;
export type RecommendationStatus = (typeof RECOMMENDATION_STATUSES)[number];

export function isRecommendationStatus(value: string): value is RecommendationStatus {
  return (RECOMMENDATION_STATUSES as readonly string[]).includes(value);
}

/** Provenance of a recommendation (hybrid AI/derived → coach review). */
export const RECOMMENDATION_SOURCES = ['ai', 'coach_adjusted'] as const;
export type RecommendationSource = (typeof RECOMMENDATION_SOURCES)[number];

export function isRecommendationSource(value: string): value is RecommendationSource {
  return (RECOMMENDATION_SOURCES as readonly string[]).includes(value);
}

/**
 * Coaching engagement lifecycle (CR5). A tiny state machine — NO booking/payment
 * states (J3 non-goal). Selection = creation.
 *   active → paused → completed
 */
export const ENGAGEMENT_STATUSES = ['active', 'paused', 'completed'] as const;
export type EngagementStatus = (typeof ENGAGEMENT_STATUSES)[number];

export function isEngagementStatus(value: string): value is EngagementStatus {
  return (ENGAGEMENT_STATUSES as readonly string[]).includes(value);
}

/**
 * Coaching session lifecycle (CR5). Sessions are coach-maintained TRACKING
 * records, NOT a booking system (J3): `scheduledFor` is a coach memo date.
 *   scheduled → done (or cancelled)
 */
export const SESSION_STATUSES = ['scheduled', 'done', 'cancelled'] as const;
export type SessionStatus = (typeof SESSION_STATUSES)[number];

export function isSessionStatus(value: string): value is SessionStatus {
  return (SESSION_STATUSES as readonly string[]).includes(value);
}
